import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { DateFilter, DateFilterValue } from '../../components/common';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import {
  TrendingUp,
  AttachMoney,
  Download,
  Receipt,
  Business,
} from '@mui/icons-material';
import { useSuperadminRevenueReport } from '../../hooks/useSuperAdmin';
import { colors } from '../../theme/theme';

export const RevenueReportPage = () => {
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('all');
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const { report, isLoading, error } = useSuperadminRevenueReport({
    date_filter: dateFilter,
    start_date: startDate,
    end_date: endDate,
  });

  const handleDateFilterChange = (filter: DateFilterValue, start?: string, end?: string) => {
    setDateFilter(filter);
    setStartDate(start);
    setEndDate(end);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error">Failed to load revenue report</Alert>
      </AdminLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const columns: GridColDef[] = [
    {
      field: 'county_name',
      headerName: 'County',
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Business sx={{ fontSize: 20, color: colors.primary.main }} />
          <Typography fontWeight={500}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Total Revenue',
      flex: 1,
      renderCell: (params) => (
        <Typography fontWeight={600} color={colors.success.main}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'transactions',
      headerName: 'Transactions',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: colors.primary.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'avg_transaction',
      headerName: 'Avg Transaction',
      flex: 1,
      valueGetter: (value, row) => {
        return row.transactions > 0 ? row.revenue / row.transactions : 0;
      },
      renderCell: (params) => formatCurrency(params.value),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            System Revenue Reports
          </Typography>
          <Box display="flex" gap={2}>
            <DateFilter
              value={dateFilter}
              onChange={handleDateFilterChange}
              startDate={startDate}
              endDate={endDate}
            />
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                },
              }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {formatCurrency(report?.totalRevenue || 0)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      This Month
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {formatCurrency(report?.revenueThisMonth || 0)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Today
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {formatCurrency(report?.revenueToday || 0)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Revenue by County
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={report?.revenueByCounty || []}
                columns={columns}
                getRowId={(row) => row.county_id}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
